/**
 * deployment-edit.js
 */

import React from 'react';
import axios from 'axios';
import {
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  useHistory,
} from "react-router-dom";
import { useForm, FormProvider } from 'react-hook-form';
import { formatISO, parse, addHours } from 'date-fns';
import { useSnackbar } from 'notistack';
import { Types } from 'utils/constants';
import { dateParamFormat } from 'utils/datetime';
import { makeLocation } from 'utils/routing';
import useMounted from 'utils/use-mounted';
import { objectToFormData } from 'utils/form-data';
import { shouldRenderMobileLayout, shouldRenderMobileInDesktopDrawer } from 'utils/app-context';
import useRecordStore from 'contexts/record-store';
import DropMenu from 'components/drop-menu';
import Icon from 'components/icon';
import ConfirmUnsavedEditor from 'components/menu-drawer/confirm-unsaved-editor';
import DrawerActionButton from 'components/menu-drawer/drawer-action-button';
import { DetectionOdorsContext } from 'components/detection-odors/context/detection-odors-context';
import getWizardState from '../helpers/get-wizard-state';


export default function DeploymentEditForm(props) {
  const {
    deploymentId,
    sectionType,
    sectionPath,
    basePath,
    drawerState,
    edit,
    patrolIndex,
    step,
    onPatrolDelete,
    setOnSaveManualTrigger,
    onClose,
  } = props;
  const storedRecord = edit.record;
  const setRecord = edit.setRecord;
  const options = edit.options;
  const setIsContentRendered = edit.setIsContentRendered;
  const history = useHistory();
  const isMounted = useMounted();
  const { enqueueSnackbar } = useSnackbar();
  const { currentDoneHandler } = React.useContext(DetectionOdorsContext);
  const recordStore = useRecordStore();
  const deploymentRecord = recordStore.useRecord(Types.Record.DEPLOYMENT, deploymentId);
  const hasRecord = Boolean(deploymentRecord.get());
  const patrolTypeCount = hasRecord && deploymentRecord.get().workMode === Types.WorkMode.PATROL
    && deploymentRecord.get().patrol.patrolTypes ? deploymentRecord.get().patrol.patrolTypes.length : 0;
  const [isReady, setIsReady] = React.useState(false);
  const [submittedState, setSubmittedState] = React.useState();

  // hook form
  const formContext = useForm();
  const { formState, handleSubmit, reset } = formContext;
  const dirtyFields = formState.dirtyFields;


  const wizardState = React.useMemo(() => (
    getWizardState(deploymentRecord.get(), step, basePath, history)

  // deploymentRecord is hookstate getter
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ), [hasRecord, sectionPath, patrolTypeCount, step, basePath, history]);


  const hideInvalidOptions = (optionGroupArray) => {
    return optionGroupArray.map(option =>
      option.name === Types.Deployment.ETHNICITIES_SEXES_NA_TEXT
        ? { ...option, hidden: true }
        : option);
  };


  // get deployment options on init
  React.useEffect(() => {
    if (isReady) {
      return;
    }

    const filteredReset = (object, options) => {
      // Remove review from form data. With react-hook-form v7 this 
      // get stored and included with submission data even though there is no corresponding form field,
      // causing intermittent db conflict errors. Using shouldUnregister in useForm prevents this
      // but causes other problems.
      const { review: _, ...formInitData } = object;
      reset(formInitData, options);
    };

    // check if we have already done this and have cached results
    if (storedRecord && options) {
      filteredReset(storedRecord);
      setIsReady(true);
      setIsContentRendered(true);
      return;
    }

    setTimeout(() => {
      drawerState.loading.content.set(true);
      setIsContentRendered(false);
    });
    (async () => {
      try {
        if (deploymentId >= 0) {
          const dateParam = new URLSearchParams(window.location.search).get('date');
          const date = formatISO(dateParam
            ? addHours(parse(dateParam, dateParamFormat, new Date()), 10)
            : new Date());
          const response = await axios.get(`/deployment/${deploymentId}/edit`, {
            params: { date },
          });

          if (!isMounted.current) return;
          if (response.status !== 200) return;

          const deployment = JSON.parse(JSON.stringify(recordStore.updateRecord(response.data.deployment).get()));
          filteredReset(deployment);

          response.data.options.ethnicities = hideInvalidOptions(response.data.options.ethnicities);
          response.data.options.sexes = hideInvalidOptions(response.data.options.sexes);
          response.data.options.ethnicitiesNAID = response.data.options.ethnicities.find(eth => eth.name === Types.Deployment.ETHNICITIES_SEXES_NA_TEXT).id;
          response.data.options.sexesNAID = response.data.options.sexes.find(sex => sex.name === Types.Deployment.ETHNICITIES_SEXES_NA_TEXT).id;
          edit.setOptions(response.data.options);

          edit.setRecord(deployment);
          setIsReady(true);

          drawerState.loading.content.set(false);
          setIsContentRendered(true);
        }
      } catch (e) {
        if (e.response && e.response.status === 404) {
          return history.push(basePath);
        }
        if (e.response && e.response.status === 403) {
          return history.push(makeViewLocation(deploymentId));
        }
      }
    })();

  // recordStore & drawerState & exerciseRecord are hookstates
  // observe isReady (and other dependencies) but changes shouldn't trigger the effect
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deploymentId, storedRecord, basePath, history, isMounted, reset, setIsContentRendered]);


  const restoreOriginal = () => {
    if (storedRecord) {
      deploymentRecord.set(storedRecord);
    }
  };


  const makeViewLocation = React.useCallback((deploymentId) => {
    return makeLocation(
      basePath,
      'deployment',
      'view',
      {
        deploymentId,
        viewSectionId: sectionPath,
      },
      history.location
    );
  }, [basePath, sectionPath, history.location]);


  // Ensure that ConfirmUnsavedEditor props are up to date by triggering 
  // a re-render before proceding with save and navigation.
  // (onSubmit > useEffect > afterSubmit)
  const onSubmit = React.useCallback((data, followAction) => {
    setSubmittedState({ data, followAction });
  }, []);


  const afterSubmit = React.useCallback(async (data, followAction) => {
    const saveRecord = {
      ...storedRecord,
      ...data,
      sectionType,
      patrolIndex
    };

    // handle patrol type array merging
    if (patrolIndex !== undefined) {
      const newPatrol = {...data.patrol.patrolTypes[patrolIndex]};
      saveRecord.patrol.patrolTypes = [...storedRecord.patrol.patrolTypes];
      saveRecord.patrol.patrolTypes[patrolIndex] = newPatrol;
    }

    const formData = objectToFormData(saveRecord, '', []);
    drawerState.loading.content.set(true);

    try {
      const isNewDeployment = storedRecord.id === 0;
      const prevDeploymentDate = isNewDeployment ? null : storedRecord.date;
      const { record: savedDeployment } = await recordStore.saveDeployment(deploymentId, formData, prevDeploymentDate);

      if (savedDeployment.get()) {
        setRecord(JSON.parse(JSON.stringify(savedDeployment.get())));

        // DONE
        if (followAction === 'done') {
          onClose();
        }

        // NEXT
        else if (followAction === 'next') {
          const wizardState = getWizardState(savedDeployment.get(), step, basePath, history);
          if (wizardState.nextLocation) {
            history.push(wizardState.nextLocation);
          }
        }

        // NEW PATROL TYPE
        else if (followAction === 'new-patrol') {
          // attempt to scroll view-section back into frame
          window.requestAnimationFrame(() => {
            const element = document.querySelector(`#section-${savedDeployment.get().id}-${sectionPath}`);
            if (element) {
              setTimeout(() => element.scrollIntoView({ behavior: 'smooth' }), 100);
            }
          });
        }

        // REGULAR SAVE
        else {
          enqueueSnackbar('Deployment saved', { variant: 'success' });
          if (followAction !== 'none') {
            history.push(makeViewLocation(savedDeployment.get().id));
            // attempt to scroll view-section back into frame
            window.requestAnimationFrame(() => {
              const element = document.querySelector(`#section-${savedDeployment.get().id}-${sectionPath}`);
              if (element) {
                setTimeout(() => element.scrollIntoView({ behavior: 'smooth' }), 100);
              }
            });
          }
        }
      }
    } catch (e) {
      enqueueSnackbar('Error saving deployment', {
        variant: 'error',
        persist: true,
        anchorOrigin: {
          vertical: "top",
          horizontal: "center"
        }
      });
    } finally {
      drawerState.loading.content.set(false);
    }

  // recordStore & drawerState cause infinite loop
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    deploymentId,
    patrolIndex,
    sectionPath,
    sectionType,
    storedRecord,
    setRecord,
    step,
    basePath,
    history,
    dirtyFields,
    onClose,
    makeViewLocation,
    enqueueSnackbar
  ]);

  
  React.useEffect(() => {
    if (submittedState) {
      afterSubmit(submittedState.data, submittedState.followAction);
    }
  // only call afterSubmit when submittedState changes, not when afterSubmit itself changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submittedState]);


  const onSave = React.useCallback((followAction) => {
    handleSubmit(
      (data) => onSubmit(data, followAction),
      () => {}
    )();
  }, [handleSubmit, onSubmit]);


  const onSaveSync = React.useCallback(async (followAction) => {
    await handleSubmit(
      (data) => onSubmit(data, followAction),
      () => {}
    )();
  }, [handleSubmit, onSubmit]);


  React.useEffect(() => {
    if (setOnSaveManualTrigger) {
      setOnSaveManualTrigger(() => async () => await onSaveSync('new-patrol'));
    }
  }, [setOnSaveManualTrigger, onSaveSync]);


  const onCancel = React.useCallback(() => {
    if (deploymentId === 0) {
      history.goBack();
    } else {
      history.push(makeViewLocation(deploymentId));
    }
  }, [history, deploymentId, makeViewLocation]);


  const cancelButton = React.useMemo(() => (
    <Tooltip
      arrow
      title='Abandon edits in this section'
      placement='top'
    >
      <span>
        <DrawerActionButton
          onClick={onCancel}
          style={{ color: 'inherit' }}
          data-testid='deployment-section-cancel-button'
        >
          Cancel
        </DrawerActionButton>
      </span>
    </Tooltip>
  ), [onCancel]);


  const saveButton = React.useMemo(() => (
    <Tooltip
      arrow
      title={wizardState.hasNext
        ? 'Save this section and advance to the next step'
        : 'Save this section'}
      placement='top'
      style={{ marginLeft: 16 }}
    >
      <span>
        <DrawerActionButton
          level={1}
          data-testid='deployment-section-save-button'
          onClick={async () => {
            if (currentDoneHandler && !(await currentDoneHandler())) {
              return;
            }
            onSave(wizardState.hasNext ? 'next' : '');
          }}
        >
          {wizardState.hasNext ? 'Next' : 'Save'}
        </DrawerActionButton>
      </span>
    </Tooltip>
  ), [wizardState, currentDoneHandler, onSave]);


  const doneButton = React.useMemo(() => (
    <>
      {wizardState.hasNext &&
      <Tooltip
        arrow
        title='Save this section and close the record editor'
        placement='top'
        style={{ marginLeft: 16 }}
      >
        <span>
          <DrawerActionButton
            level={2}
            onClick={async () => {
              if (currentDoneHandler && !(await currentDoneHandler())) {
                return;
              }
              onSave('done');
            }}
            style={{ color: 'inherit' }}
          >
            Done
          </DrawerActionButton>
        </span>
      </Tooltip>}
    </>
  ), [wizardState, currentDoneHandler, onSave]);


  const dropMenuItems = () => {
    let items = [];

    // done button
    if (wizardState.hasNext) {
      items.push({
        name: 'Done',
        key: 'done',
        onSelect: async () => {
          if (currentDoneHandler && !(await currentDoneHandler())) {
            return;
          }
          onSave('done');
        }
      });
    }

    items.push({
      name: 'Cancel',
      key: 'cancel',
      'data-testid': 'deployment-section-cancel-drop-menu-button',
      onSelect: () => onCancel()
    });

    if (onPatrolDelete) {
      items.push({
        name: 'Delete',
        key: 'delete',
        'data-testid': 'deployment-section-delete-drop-menu-button',
        onSelect: () => onPatrolDelete()
      });
    }

    return items;
  }


  React.useEffect(() => {
    if (edit.setActions) {
      const useDropMenu = shouldRenderMobileLayout() || shouldRenderMobileInDesktopDrawer();
      edit.setActions(
        <div onClick={e => e.stopPropagation()}>
          {useDropMenu &&
          <>
            {saveButton}
            <DropMenu
              id={`deployment-edit-actions`}
              items={dropMenuItems()}
              label={(
                <IconButton
                  size="small"
                  style={{color: 'inherit', marginLeft: 8 }}
                  data-testid='deployment-open-drop-menu-button'
                >
                  <Icon type='menu' />
                </IconButton>
              )}
            />
          </>}

          {!useDropMenu &&
          <>{cancelButton} {doneButton} {saveButton}</>}
        </div>
      );
    }

  // it complains about edit.setActions even though it's in the array
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [edit.setActions, cancelButton, saveButton, doneButton]);


  return (
    <>
      {isReady &&
      <>
        <FormProvider {...formContext}>
          <form onSubmit={handleSubmit(onSubmit)}>

            {props.edit.record &&
            <edit.component {...props} />}

          </form>
        </FormProvider>
      </>}

      <ConfirmUnsavedEditor
        dirty={formState.isDirty && !formState.isSubmitted}
        discard={restoreOriginal}
        save={async () => {
          return await new Promise((resolve) => {
            handleSubmit(
              async (data) => {
                onSubmit(data, 'none');
                return resolve(true);
              },
              () => {
                return resolve(false);
              })();
          });
        }}
      />
    </>
  );
}